import { useState } from 'react';
import { Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { FieldProse, NumberedSection, PanelCard, RegaraNoteList } from './DossierPrimitives';
import {
  fetchDeclarationOfConformity,
  type DeclarationOfConformity,
  type DocManufacturer,
  type DocProductId,
  type DocNotifiedBody,
  type DocSignatory,
} from '@/features/dossier/api';

/**
 * EU MDR Declaration of Conformity panel (Annex IV).
 *
 * Split into:
 *   - `DeclarationOfConformityRenderer` — presentational, accepts a
 *     `DeclarationOfConformity` as a prop. Unit-testable without fetch.
 *   - `DeclarationOfConformityPanel` — container; fetches via
 *     `fetchDeclarationOfConformity` and delegates to the renderer.
 *
 * Renders one numbered section per Annex IV item:
 *   §1  Manufacturer
 *   §2  Statement of Sole Responsibility
 *   §3  Basic UDI-DI
 *   §4  Product Identification
 *   §5  Risk Class
 *   §6  Declaration of Conformity
 *   §7  Standards & CS Applied
 *   §8  Notified Body
 *   §9  Additional Information
 *   §10 Signatory
 *   Notes (when present)
 *   Download PDF / DOCX links targeting
 *     GET …/declaration-of-conformity?device_class=…&format=pdf|docx
 *
 * Types are single-sourced in `@/features/dossier/api` — NOT re-declared here.
 */

// Re-export types so existing importers (e.g. tests) keep resolving.
export type { DeclarationOfConformity, DocManufacturer, DocProductId, DocNotifiedBody, DocSignatory };

// ── Null-identifier placeholder ───────────────────────────────────────────────

const PENDING = '[To be completed by the manufacturer]';

function NullField({ label, value }: { label: string; value: string | null | undefined }) {
  if (value) {
    return (
      <div className="grid grid-cols-[minmax(180px,220px)_1fr] gap-x-4 gap-y-0.5 my-2">
        <div className="font-semibold text-ink/80 text-[13px]">{label}</div>
        <div className="text-[13.5px] min-w-0">{value}</div>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-[minmax(180px,220px)_1fr] gap-x-4 gap-y-0.5 my-2">
      <div className="font-semibold text-ink/80 text-[13px]">{label}</div>
      <div className="text-[13.5px] min-w-0">
        <span className="text-muted italic">{PENDING}</span>
      </div>
    </div>
  );
}

// ── Presentational renderer ───────────────────────────────────────────────────

export function DeclarationOfConformityRenderer({
  result,
  productId,
  deviceClass,
  hideDownload = false,
  regulation = 'MDR',
}: {
  result: DeclarationOfConformity;
  productId: number;
  deviceClass: string;
  hideDownload?: boolean;
  /** Cover framing only — 'MDR' (default, byte-identical) or 'IVDR' (Reg. 2017/746). */
  regulation?: 'MDR' | 'IVDR';
}) {
  const baseUrl = `/api/products/${productId}/declaration-of-conformity?device_class=${encodeURIComponent(deviceClass)}`;

  return (
    <div className="space-y-4">
      <DocCoverBlock deviceClass={result.device_class} regulation={regulation} />

      <Section1Manufacturer manufacturer={result.manufacturer} />
      <Section2SoleResponsibility statement={result.sole_responsibility_statement} />
      <Section3BasicUdiDi basicUdiDi={result.basic_udi_di} />
      <Section4ProductIdentification productId={result.product_identification} />
      <Section5RiskClass statement={result.risk_class_statement} />
      <Section6ConformityStatement statement={result.conformity_statement} />
      <Section7StandardsAndCs standards={result.standards_and_cs_applied} />
      <Section8NotifiedBody notifiedBody={result.notified_body} />

      <Section9AdditionalInformation info={result.additional_information} />

      <Section10Signatory signatory={result.signatory} />

      <RegaraNoteList notes={result.notes} />

      {!hideDownload && <DownloadBar baseUrl={baseUrl} />}
    </div>
  );
}

// ── Cover block ───────────────────────────────────────────────────────────────

function DocCoverBlock({
  deviceClass,
  regulation,
}: {
  deviceClass: string;
  regulation: 'MDR' | 'IVDR';
}) {
  const regNum = regulation === 'IVDR' ? '2017/746' : '2017/745';
  return (
    <PanelCard>
      <div className="text-xs tracking-[0.16em] uppercase text-muted font-bold mb-1.5">
        EU {regulation} Declaration of Conformity
      </div>
      <p className="text-[11px] text-muted m-0 mb-4">
        Regulation (EU) {regNum} — Annex IV
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="bg-[#f8f9fa] rounded-md py-2.5 px-3">
          <div className="text-[10px] uppercase tracking-[0.12em] font-bold text-muted mb-0.5">
            Device class (EU {regulation})
          </div>
          <div className="text-[13px] text-navy font-semibold">{deviceClass}</div>
        </div>
      </div>
    </PanelCard>
  );
}

// ── §1 Manufacturer ───────────────────────────────────────────────────────────

function Section1Manufacturer({ manufacturer }: { manufacturer: DocManufacturer }) {
  return (
    <NumberedSection prefix="Section" num={1} title="Manufacturer">
      <NullField label="Name:" value={manufacturer.name} />
      <NullField label="Single Registration Number (SRN):" value={manufacturer.srn} />
      <NullField label="Address:" value={manufacturer.address} />
      <NullField label="Authorised representative:" value={manufacturer.authorised_representative} />
    </NumberedSection>
  );
}

// ── §2 Statement of Sole Responsibility ──────────────────────────────────────

function Section2SoleResponsibility({ statement }: { statement: string }) {
  return (
    <NumberedSection prefix="Section" num={2} title="Statement of Sole Responsibility">
      <FieldProse label="Statement:" body={statement} fallbackHint="Sole responsibility statement" />
    </NumberedSection>
  );
}

// ── §3 Basic UDI-DI ──────────────────────────────────────────────────────────

function Section3BasicUdiDi({ basicUdiDi }: { basicUdiDi: string | null }) {
  return (
    <NumberedSection prefix="Section" num={3} title="Basic UDI-DI">
      <NullField label="Basic UDI-DI:" value={basicUdiDi} />
    </NumberedSection>
  );
}

// ── §4 Product Identification ─────────────────────────────────────────────────

function Section4ProductIdentification({ productId }: { productId: DocProductId }) {
  return (
    <NumberedSection prefix="Section" num={4} title="Product Identification">
      <NullField label="Product name:" value={productId.product_name} />
      <NullField label="Trade name:" value={productId.trade_name} />
      <NullField label="Product code / catalogue reference:" value={productId.product_code} />
    </NumberedSection>
  );
}

// ── §5 Risk Class ─────────────────────────────────────────────────────────────

function Section5RiskClass({ statement }: { statement: string }) {
  return (
    <NumberedSection prefix="Section" num={5} title="Risk Class">
      <FieldProse label="Risk class statement:" body={statement} fallbackHint="Risk classification per Annex VIII" />
    </NumberedSection>
  );
}

// ── §6 Declaration of Conformity ─────────────────────────────────────────────

function Section6ConformityStatement({ statement }: { statement: string }) {
  return (
    <NumberedSection prefix="Section" num={6} title="Declaration of Conformity">
      <FieldProse label="Statement:" body={statement} fallbackHint="Declaration of conformity statement" />
    </NumberedSection>
  );
}

// ── §7 Standards & CS Applied ─────────────────────────────────────────────────

function Section7StandardsAndCs({ standards }: { standards: string[] }) {
  return (
    <NumberedSection prefix="Section" num={7} title="Standards and Common Specifications Applied">
      {standards.length > 0 ? (
        <ul className="list-disc pl-5 m-0 space-y-1 text-[13.5px]">
          {standards.map((s, i) => (
            <li key={i}>{s}</li>
          ))}
        </ul>
      ) : (
        <p className="text-[13.5px] text-muted italic m-0">{PENDING}</p>
      )}
    </NumberedSection>
  );
}

// ── §8 Notified Body ──────────────────────────────────────────────────────────

function Section8NotifiedBody({ notifiedBody }: { notifiedBody: DocNotifiedBody }) {
  return (
    <NumberedSection prefix="Section" num={8} title="Notified Body">
      <NullField label="Name:" value={notifiedBody.name} />
      <NullField label="Identification number:" value={notifiedBody.identification_number} />
      <NullField label="Certificate reference:" value={notifiedBody.certificate_reference} />
      <FieldProse
        label="Conformity assessment route:"
        body={notifiedBody.conformity_assessment_route}
        fallbackHint="Conformity assessment route"
      />
    </NumberedSection>
  );
}

// ── §9 Additional Information ─────────────────────────────────────────────────

function Section9AdditionalInformation({ info }: { info: string | null | undefined }) {
  return (
    <NumberedSection prefix="Section" num={9} title="Additional Information">
      {info ? (
        <FieldProse label="Information:" body={info} fallbackHint="Additional information" />
      ) : (
        <p className="text-[13.5px] text-muted italic m-0">{PENDING}</p>
      )}
    </NumberedSection>
  );
}

// ── §10 Signatory ─────────────────────────────────────────────────────────────

function Section10Signatory({ signatory }: { signatory: DocSignatory }) {
  return (
    <NumberedSection prefix="Section" num={10} title="Signatory">
      <NullField label="Place:" value={signatory.place} />
      <NullField label="Date:" value={signatory.date} />
      <NullField label="Name:" value={signatory.name} />
      <NullField label="Function:" value={signatory.function} />
      <NullField label="On behalf of:" value={signatory.on_behalf_of} />
    </NumberedSection>
  );
}

// ── Download bar ──────────────────────────────────────────────────────────────

function DownloadBar({ baseUrl }: { baseUrl: string }) {
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <Button variant="brand" size="sm" asChild>
        <a
          href={`${baseUrl}&format=pdf`}
          target="_blank"
          rel="noopener"
          title="Download Declaration of Conformity as PDF"
        >
          <Download className="size-3.5" /> Download PDF
        </a>
      </Button>
    </div>
  );
}

// ── Container (fetches data) ──────────────────────────────────────────────────

type FetchState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ok'; result: DeclarationOfConformity };

export function DeclarationOfConformityPanel({
  productId,
  deviceClass,
}: {
  productId: number;
  deviceClass: string;
}) {
  const [state, setState] = useState<FetchState>({ status: 'idle' });

  const load = async () => {
    setState({ status: 'loading' });
    try {
      const result = await fetchDeclarationOfConformity(productId, deviceClass);
      setState({ status: 'ok', result });
    } catch (err) {
      setState({
        status: 'error',
        message: err instanceof Error ? err.message : 'Failed to load declaration of conformity.',
      });
    }
  };

  if (state.status === 'idle') {
    return (
      <PanelCard>
        <div className="text-xs tracking-[0.16em] uppercase text-muted font-bold mb-3">
          EU MDR Declaration of Conformity
        </div>
        <p className="text-[13px] text-muted m-0 mb-4">
          Generate a Declaration of Conformity summary for this device class.
        </p>
        <Button variant="brand" size="sm" onClick={() => void load()}>
          Load Declaration of Conformity
        </Button>
      </PanelCard>
    );
  }

  if (state.status === 'loading') {
    return (
      <PanelCard>
        <p className="text-[13px] text-muted italic m-0">
          Loading declaration of conformity…
        </p>
      </PanelCard>
    );
  }

  if (state.status === 'error') {
    return (
      <div className="bg-state-error-bg border border-state-error-border rounded-2xl p-6">
        <div className="font-semibold text-state-error-text text-[14px] mb-1">
          Could not load declaration of conformity
        </div>
        <p className="text-[13px] text-state-error-text m-0 mb-3">{state.message}</p>
        <Button variant="outline" size="sm" onClick={() => void load()}>
          Retry
        </Button>
      </div>
    );
  }

  return (
    <DeclarationOfConformityRenderer
      result={state.result}
      productId={productId}
      deviceClass={deviceClass}
    />
  );
}
